import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { XMarkIcon, CheckCircleIcon, ExclamationTriangleIcon, ArrowPathIcon, CloudArrowUpIcon } from '@heroicons/react/24/outline';
import Button from '../Button';
import { NFSServer } from '../../types';

const NFS_DEPLOY_STEPS = [
  { name: 'Ensuring share directories', message: 'Ensuring share directories...' },
  { name: 'Creating backup', message: 'Creating backup...' },
  { name: 'Writing new configuration', message: 'Writing new configuration...' },
  { name: 'Reloading NFS service', message: 'Reloading NFS service...' },
  { name: 'Verifying NFS service status', message: 'Verifying NFS service status...' },
];

const StepStatus = {
  PENDING: 'pending',
  IN_PROGRESS: 'in_progress',
  COMPLETED: 'success',
  FAILED: 'error',
};

type DeployModalProps = {
  isOpen: boolean;
  onClose: () => void;
  servers: NFSServer[];
  sseService: any;
  nfsApi: any;
};

function formatDurationUs(durationUs?: number): string {
  if (typeof durationUs !== 'number') return '';
  if (durationUs < 1000) return `${durationUs}µs`;
  if (durationUs < 1_000_000) return `${(durationUs / 1000).toFixed(1)}ms`;
  return `${(durationUs / 1_000_000).toFixed(2)}s`;
}

function getOverallStepStatus(serverSteps: Record<string, any[]>, servers: NFSServer[], idx: number) {
  let hasError = false, hasInProgress = false, allCompleted = true;
  for (const server of servers) {
    const step = (serverSteps[server.id] || [])[idx];
    if (!step || step.status === StepStatus.PENDING) allCompleted = false;
    if (step && step.status === StepStatus.FAILED) hasError = true;
    if (step && step.status === StepStatus.IN_PROGRESS) hasInProgress = true;
    if (step && step.status !== StepStatus.COMPLETED) allCompleted = false;
  }
  if (hasError) return StepStatus.FAILED;
  if (hasInProgress) return StepStatus.IN_PROGRESS;
  if (allCompleted) return StepStatus.COMPLETED;
  return StepStatus.PENDING;
}

function getMaxStepDuration(serverSteps: Record<string, any[]>, servers: NFSServer[], idx: number) {
  let max = 0;
  for (const server of servers) {
    const step = (serverSteps[server.id] || [])[idx];
    if (step && step.durationUs && step.durationUs > max) max = step.durationUs;
  }
  return max;
}

function getStepError(serverSteps: Record<string, any[]>, servers: NFSServer[], idx: number) {
  for (const server of servers) {
    const step = (serverSteps[server.id] || [])[idx];
    if (step && step.status === StepStatus.FAILED && step.message) return step.message;
  }
  return '';
}

function getStepStatusIcon(status: string) {
  if (status === StepStatus.COMPLETED) return <CheckCircleIcon className="h-5 w-5 text-emerald-500" />;
  if (status === StepStatus.FAILED) return <ExclamationTriangleIcon className="h-5 w-5 text-rose-500" />;
  if (status === StepStatus.IN_PROGRESS) return <ArrowPathIcon className="h-5 w-5 text-sky-500 animate-spin" />;
  return <div className="h-5 w-5 rounded-full border-2 border-zinc-700"></div>;
}

enum DeployStep {
  CONFIRM = 'confirm',
  PROGRESS = 'progress',
  COMPLETE = 'complete',
}

const DeployModal: React.FC<DeployModalProps> = ({ isOpen, onClose, servers, sseService, nfsApi }) => {
  const [step, setStep] = useState<DeployStep>(DeployStep.CONFIRM);
  const [progress, setProgress] = useState(0);
  const [isDeploying, setIsDeploying] = useState(false);
  const [serverSteps, setServerSteps] = useState<Record<string, any[]>>({});
  const [completedServers, setCompletedServers] = useState<string[]>([]);
  const [failedServers, setFailedServers] = useState<string[]>([]);
  const closeTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
  const wasOpen = useRef(false);

  const handleClose = () => {
    onClose();
    if (closeTimeout.current) clearTimeout(closeTimeout.current);
    closeTimeout.current = setTimeout(() => {
      setStep(DeployStep.CONFIRM);
      setProgress(0);
      setIsDeploying(false);
      setServerSteps({});
      setCompletedServers([]);
      setFailedServers([]);
    }, 300); // match HAProxy modal close animation duration
  };

  useEffect(() => {
    if (isOpen && !wasOpen.current) {
      // Modal just opened, reset state immediately
      setStep(DeployStep.CONFIRM);
      setProgress(0);
      setIsDeploying(false);
      setServerSteps({});
      setCompletedServers([]);
      setFailedServers([]);
    }
    wasOpen.current = isOpen;
    return () => {
      if (closeTimeout.current) clearTimeout(closeTimeout.current);
    };
  }, [isOpen]);

  useEffect(() => {
    if (step !== DeployStep.PROGRESS) return;
    const handleDeploymentStatus = (data: any) => {
      const { serverId, status, step: stepNum, error, durationUs } = data;
      setServerSteps(prev => {
        const steps = prev[serverId] ? [...prev[serverId]] : NFS_DEPLOY_STEPS.map(s => ({ ...s, status: 'pending', durationUs: undefined, duration: undefined, message: s.message }));
        const idx = stepNum - 1;
        if (!steps[idx]) return prev;
        if (status === 'in_progress') {
          steps[idx] = { ...steps[idx], status: 'in_progress', durationUs: undefined, duration: undefined, message: NFS_DEPLOY_STEPS[idx].message };
        } else if (status === 'success') {
          steps[idx] = { ...steps[idx], status: 'success', durationUs, duration: formatDurationUs(durationUs), message: 'Completed successfully' };
          if (idx + 1 < steps.length) {
            steps[idx + 1] = { ...steps[idx + 1], status: 'in_progress', durationUs: undefined, duration: undefined };
          }
        } else if (status === 'error') {
          steps[idx] = { ...steps[idx], status: 'error', durationUs, duration: formatDurationUs(durationUs), message: error || 'Failed to complete step' };
        }
        return { ...prev, [serverId]: steps };
      });
      if ((status === 'success' || status === 'error') && stepNum === NFS_DEPLOY_STEPS.length) {
        if (status === 'success') {
          setCompletedServers(prev => [...prev, serverId]);
        } else {
          setFailedServers(prev => [...prev, serverId]);
        }
      }
    };
    sseService.on('deployment_status', handleDeploymentStatus);
    return () => sseService.off('deployment_status', handleDeploymentStatus);
  }, [step, servers, completedServers.length, failedServers.length, serverSteps]);

  // Compute finished server count based on serverSteps
  const finishedServerCount = useMemo(() => {
    return servers.filter(server => {
      const steps = serverSteps[server.id];
      if (!steps) return false;
      const lastStep = steps[steps.length - 1];
      return lastStep && (lastStep.status === 'success' || lastStep.status === 'error');
    }).length;
  }, [serverSteps, servers]);

  useEffect(() => {
    if (step === DeployStep.PROGRESS && finishedServerCount === servers.length && servers.length > 0) {
      setStep(DeployStep.COMPLETE);
      setIsDeploying(false);
    }
  }, [step, finishedServerCount, servers.length]);

  useEffect(() => {
    const totalSteps = NFS_DEPLOY_STEPS.length * servers.length;
    const completed = Object.values(serverSteps).reduce(
      (acc, steps) => acc + steps.filter(s => s.status === 'success' || s.status === 'error').length,
      0
    );
    setProgress(Math.round((completed / totalSteps) * 100));
  }, [serverSteps, servers]);

  const handleDeploy = async () => {
    setStep(DeployStep.PROGRESS);
    setIsDeploying(true);
    await nfsApi.deploy();
  };

  // Confirm Step
  const renderConfirmStep = () => (
    <div className="p-6">
      <div className="flex flex-col items-center">
        <CloudArrowUpIcon className="h-12 w-12 text-blue-500 mb-4 mx-auto" />
        <h3 className="text-lg font-medium text-zinc-100 mb-2">Deploy NFS Configuration</h3>
        <p className="text-sm text-zinc-400 mb-4 text-center">
          You are about to deploy the current NFS configuration to all <span className="font-semibold text-zinc-100">{servers.length}</span> server{servers.length === 1 ? '' : 's'}.
          This action will reload the NFS service on each server.
        </p>
      </div>
      <div className="mt-5 sm:mt-6 sm:grid sm:grid-flow-row-dense sm:grid-cols-2 sm:gap-3">
        <Button
          variant="primary"
          onClick={handleDeploy}
          disabled={isDeploying}
          className="sm:col-start-2"
        >
          Deploy
        </Button>
        <button
          type="button"
          className="mt-3 inline-flex w-full justify-center rounded-md bg-zinc-700 px-3 py-2 text-sm font-semibold text-zinc-300 shadow-sm hover:bg-zinc-600 sm:col-start-1 sm:mt-0"
          onClick={handleClose}
        >
          Cancel
        </button>
      </div>
    </div>
  );

  // Progress Step
  const renderProgressStep = () => (
    <div className="p-6">
      <h3 className="text-lg font-medium text-zinc-100 mb-2">Deployment in Progress</h3>
      <p className="text-sm text-zinc-400 mb-4">
        Deploying configuration to {servers.length} server{servers.length === 1 ? '' : 's'}
      </p>
      <div className="mb-6">
        <div className="flex justify-between mb-1">
          <span className="text-xs font-medium text-zinc-400">Overall Progress</span>
          <span className="text-xs font-medium text-zinc-400">{progress}%</span>
        </div>
        <div className="w-full bg-zinc-700 rounded-full h-2">
          <div
            className="bg-indigo-600 h-2 rounded-full transition-all duration-500 ease-out"
            style={{ width: `${progress}%` }}
          ></div>
        </div>
      </div>
      <div className="mt-6">
        <h4 className="text-sm font-medium text-zinc-200 mb-2">Progress:</h4>
        <div className="space-y-5">
          {NFS_DEPLOY_STEPS.map((stepObj, idx) => {
            const status = getOverallStepStatus(serverSteps, servers, idx);
            const maxDuration = getMaxStepDuration(serverSteps, servers, idx);
            const errorMsg = getStepError(serverSteps, servers, idx);
            return (
              <div key={idx} className="flex items-start group">
                <div className="flex-shrink-0 w-6 pt-0.5">
                  {getStepStatusIcon(status)}
                </div>
                <div className="ml-4 flex-grow min-w-0">
                  <div className="text-sm font-medium text-zinc-300 group-hover:text-zinc-100 transition-colors mb-1">
                    {stepObj.name}
                  </div>
                  {status === StepStatus.FAILED && (
                    <div className="flex items-center text-xs text-rose-500 mt-1 w-full">
                      <span className="truncate flex-1" title={errorMsg}>{errorMsg}</span>
                    </div>
                  )}
                </div>
                {maxDuration > 0 && (
                  <div className="flex-shrink-0 ml-4 pt-0.5">
                    <span className="text-xs font-medium text-zinc-500">{formatDurationUs(maxDuration)}</span>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
      <div className="mt-5 sm:mt-6">
        <button
          type="button"
          className="inline-flex w-full justify-center rounded-md bg-zinc-700 px-3 py-2 text-sm font-semibold text-zinc-300 shadow-sm hover:bg-zinc-600"
          onClick={handleClose}
        >
          Cancel Deployment
        </button>
      </div>
    </div>
  );

  // Complete Step
  const renderCompleteStep = () => (
    <div className="p-6">
      <div className="text-center mb-6">
        {failedServers.length === 0 ? (
          <>
            <CheckCircleIcon className="h-12 w-12 text-green-500 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-zinc-100 mb-2">Deployment Complete</h3>
            <p className="text-sm text-zinc-400">
              Configuration successfully deployed to all {servers.length} server{servers.length === 1 ? '' : 's'}.
            </p>
          </>
        ) : (
          <>
            <ExclamationTriangleIcon className="h-12 w-12 text-yellow-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-zinc-100 mb-2">Deployment Partially Complete</h3>
            <p className="text-sm text-zinc-400">
              Configuration deployed to {completedServers.length} of {servers.length} server{servers.length === 1 ? '' : 's'}.
            </p>
          </>
        )}
      </div>
      <div className="mb-6">
        <h4 className="text-sm font-medium text-zinc-200 mb-6">Deployment Steps:</h4>
        <div className="space-y-5">
          {NFS_DEPLOY_STEPS.map((stepObj, idx) => {
            const status = getOverallStepStatus(serverSteps, servers, idx);
            const maxDuration = getMaxStepDuration(serverSteps, servers, idx);
            const errorMsg = getStepError(serverSteps, servers, idx);
            return (
              <div key={idx} className="flex items-start group">
                <div className="flex-shrink-0 w-6 pt-0.5">
                  {getStepStatusIcon(status)}
                </div>
                <div className="ml-4 flex-grow min-w-0">
                  <div className="text-sm font-medium text-zinc-300 group-hover:text-zinc-100 transition-colors mb-1">
                    {stepObj.name}
                  </div>
                  {status === StepStatus.FAILED && (
                    <div className="flex items-center text-xs text-rose-500 mt-1 w-full">
                      <span className="truncate flex-1" title={errorMsg}>{errorMsg}</span>
                    </div>
                  )}
                </div>
                {maxDuration > 0 && (
                  <div className="flex-shrink-0 ml-4 pt-0.5">
                    <span className="text-xs font-medium text-zinc-500">{formatDurationUs(maxDuration)}</span>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
      <div className="mb-6">
        <h4 className="text-sm font-medium text-zinc-200 mb-2">Deployment Summary:</h4>
        <div className="bg-zinc-800 rounded-md p-3 max-h-48 overflow-auto">
          {servers.map(server => {
            const isSuccessful = completedServers.includes(server.id);
            const isFailed = failedServers.includes(server.id);
            return (
              <div key={server.id} className="flex items-center py-1 border-b border-zinc-700 last:border-0">
                <div className={`w-2 h-2 rounded-full mr-2 ${isSuccessful ? 'bg-green-500' : isFailed ? 'bg-red-500' : 'bg-yellow-500'}`}></div>
                <span className="text-sm text-zinc-300">{server.name}</span>
                <span className="ml-auto text-xs text-zinc-500">
                  {isSuccessful ? 'Success' : isFailed ? 'Failed' : 'Pending'}
                </span>
              </div>
            );
          })}
        </div>
      </div>
      <div className="mt-5 sm:mt-6 sm:grid sm:grid-flow-row-dense sm:grid-cols-2 sm:gap-3">
        {failedServers.length > 0 && (
          <button
            type="button"
            className="inline-flex w-full justify-center rounded-md bg-red-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-red-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-red-600 sm:col-start-2"
            onClick={() => {
              setStep(DeployStep.CONFIRM);
            }}
          >
            Retry Failed
          </button>
        )}
        <button
          type="button"
          className="mt-3 inline-flex w-full justify-center rounded-md bg-zinc-700 px-3 py-2 text-sm font-semibold text-zinc-300 shadow-sm hover:bg-zinc-600 sm:col-span-2"
          onClick={handleClose}
        >
          Close
        </button>
      </div>
    </div>
  );

  return (
    <Transition.Root show={isOpen} as={React.Fragment}>
      <Dialog as="div" className="relative z-50" onClose={handleClose}>
        <Transition.Child
          as={React.Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-zinc-950 bg-opacity-75 backdrop-blur-sm transition-opacity" />
        </Transition.Child>
        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={React.Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="bg-zinc-900 rounded-xl shadow-2xl overflow-hidden w-full max-w-lg relative z-10 max-h-[90vh] flex flex-col">
                <div className="border-b border-zinc-800 px-6 py-4 flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <CloudArrowUpIcon className="h-5 w-5 text-indigo-500" />
                    <h2 className="text-lg font-medium text-zinc-100">Deploy NFS Configuration</h2>
                  </div>
                  <button
                    type="button"
                    onClick={handleClose}
                    className="rounded-md bg-transparent text-zinc-400 hover:text-zinc-200 focus:outline-none"
                  >
                    <span className="sr-only">Close</span>
                    <XMarkIcon className="h-5 w-5" aria-hidden="true" />
                  </button>
                </div>
                {step === DeployStep.CONFIRM && renderConfirmStep()}
                {step === DeployStep.PROGRESS && renderProgressStep()}
                {step === DeployStep.COMPLETE && renderCompleteStep()}
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
};

export default DeployModal; 